/**
 * Per-node expandable row in the cache debug panel.
 *
 * @module cache-debug/CacheDebugStepRow
 */
import React from 'react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { CaretRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CacheDebugStep } from './useCacheDebugData';
import { getExecutionStrategy } from './cache-debug-helpers';
import { CacheDebugActionList } from './CacheDebugActionList';
import { CacheDebugPlaybookSteps } from './CacheDebugPlaybookSteps';
import { CacheDebugFailureDetail } from './CacheDebugFailureDetail';
import { CacheDebugDataScripts } from './CacheDebugDataScripts';
import { CacheDebugFormationDiagnostics } from './CacheDebugFormationDiagnostics';

const NODE_TYPE_COLORS: Record<string, string> = {
  login: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  navigate: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  action: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  assertion: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  wait: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  setup: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  condition: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
};

type Props = {
  step: CacheDebugStep;
};

export function CacheDebugStepRow({ step }: Props) {
  const [expanded, setExpanded] = React.useState(false);
  const strategy = getExecutionStrategy(step);

  const hasExpandableContent =
    step.cache?.formationDiagnostics ||
    step.cache?.actions?.length ||
    step.debugSnapshots.length > 0 ||
    step.cacheOutcomeReason ||
    (step.stepDrivenReplay && step.stepOutcomes?.length) ||
    (step.dataScripts?.length);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded">
      <button
        onClick={() => hasExpandableContent && setExpanded(!expanded)}
        className={`w-full flex items-center gap-2 py-2 px-3 text-left text-sm ${
          hasExpandableContent ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer' : 'cursor-default'
        }`}
      >
        {hasExpandableContent ? (
          <motion.div
            animate={{ rotate: expanded ? 90 : 0 }}
            transition={{ duration: 0.15 }}
            className="flex-shrink-0"
          >
            <CaretRight size={11} className="text-muted-foreground" />
          </motion.div>
        ) : (
          <span className="w-[11px]" />
        )}

        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${strategy.dotClass}`} />

        <span className="font-medium truncate flex-1">{step.nodeName}</span>

        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border flex-shrink-0 ${NODE_TYPE_COLORS[step.nodeType] || 'bg-muted text-muted-foreground border-border'}`}>
          {step.nodeType}
        </span>

        <TooltipTrigger>
          <span className={`text-xs ${strategy.color}`}>{strategy.label}</span>
          <Tooltip>{strategy.description}</Tooltip>
        </TooltipTrigger>

        {step.cache?.playbookHealth && (
          <HealthBadge health={step.cache.playbookHealth} />
        )}
      </button>

      <AnimatePresence>
        {expanded && hasExpandableContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800">
              <ExpandedContent step={step} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ExpandedContent({ step }: { step: CacheDebugStep }) {
  const formationSection = step.cache?.formationDiagnostics ? (
    <CacheDebugFormationDiagnostics diagnostics={step.cache.formationDiagnostics} />
  ) : null;
  const dataScriptsSection = step.dataScripts?.length ? (
    <CacheDebugDataScripts scripts={step.dataScripts} />
  ) : null;

  // Step-driven replay: show per-playbook-step outcomes
  if (step.stepDrivenReplay && step.stepOutcomes?.length && step.playbookSteps?.length) {
    return (
      <>
        {formationSection}
        <CacheDebugPlaybookSteps
          playbookSteps={step.playbookSteps}
          stepOutcomes={step.stepOutcomes}
          compiledEntries={step.cache?.compiledEntries ?? null}
          actions={step.cache?.actions ?? []}
        />
        {dataScriptsSection}
      </>
    );
  }

  // Fallback: show failure details
  if (step.cacheOutcome === 'fallback' || step.debugSnapshots.length > 0) {
    return (
      <>
        {formationSection}
        <CacheDebugFailureDetail
          cacheOutcomeReason={step.cacheOutcomeReason}
          debugSnapshots={step.debugSnapshots}
          playbookSteps={step.playbookSteps}
          stepOutcomes={step.stepOutcomes}
          preEjectionSnapshot={step.preEjectionSnapshot}
        />
        {dataScriptsSection}
      </>
    );
  }

  // Cache hit without step-driven replay (legacy flat replay): show action list
  if (step.cache?.actions?.length) {
    return (
      <>
        {formationSection}
        <CacheDebugActionList actions={step.cache.actions} />
        {dataScriptsSection}
      </>
    );
  }

  // Data scripts only (no other expandable content)
  if (formationSection || dataScriptsSection) {
    return (
      <>
        {formationSection}
        {dataScriptsSection}
      </>
    );
  }

  // Miss/skipped: show reason
  if (step.cacheOutcomeReason) {
    return (
      <Text size="xs" className="text-muted-foreground">
        {step.cacheOutcomeReason}
      </Text>
    );
  }

  return null;
}

function HealthBadge({ health }: { health: 'verified' | 'degraded' | 'stale' }) {
  const classes =
    health === 'verified'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : health === 'degraded'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${classes}`}>
      {health}
    </span>
  );
}
